import { formatDistanceToNow } from "date-fns";

import { parseDeviceUA } from "./deviceUa";
import { isPrivateIp, lookupIp } from "./ipLookup";

const EMPTY_GEOJSON = {
  type: "FeatureCollection",
  features: [],
};

export function getDeviceLabel(userAgent, fallback = "Unknown") {
  if (!userAgent) return fallback;

  const parsed = parseDeviceUA(userAgent);

  if (
    parsed.app &&
    parsed.app !== "Unknown" &&
    parsed.app !== "Unknown device"
  ) {
    if (parsed.model) {
      return `${parsed.app} (${parsed.model})`;
    }

    return parsed.app;
  }

  if (parsed.type === "tv") return "Android TV";
  if (parsed.type === "phone") return "Mobile";
  if (parsed.type === "tablet") return "Tablet";
  if (parsed.type === "desktop") return "Desktop";

  return fallback;
}

function normalizeLocation(item = {}) {
  const location = item.location || {};

  if (
    Number.isFinite(location.longitude) &&
    Number.isFinite(location.latitude)
  ) {
    return {
      city: location.city || item.city || "",
      region: location.region || item.region || "",
      country: location.countryCode || location.country || item.country || "",
      latitude: location.latitude,
      longitude: location.longitude,
    };
  }

  if (Number.isFinite(item.longitude) && Number.isFinite(item.latitude)) {
    return {
      city: item.city || "",
      region: item.region || "",
      country: item.country || item.countryCode || "",
      latitude: item.latitude,
      longitude: item.longitude,
    };
  }

  return null;
}

function buildFeatureId(item, ip) {
  return (
    item.id ||
    item.registrationId ||
    item.userId ||
    item.sessionId ||
    `${ip}-${item.lastSeen || item.lastActive || item.timestamp || "live"}`
  );
}

function extractUserAgent(item = {}) {
  const metadata = item.metadata || {};
  const settings = item.settings || item.profile_settings || {};
  const candidates = [
    item.userAgent,
    item.deviceUserAgent,
    item.lunovaPlayerUserAgent,
    metadata.lunovaPlayerUserAgent,
    metadata.userAgent,
    settings.userAgent,
    item.deviceLabel,
    // Only treat device as a UA if it looks like one (contains / or spaces with version).
    typeof item.device === "string" &&
    (item.device.includes("/") || item.device.includes(" "))
      ? item.device
      : null,
  ];

  for (const value of candidates) {
    if (
      typeof value === "string" &&
      value.trim() &&
      value.trim() !== "Unknown"
    ) {
      return value.trim();
    }
  }

  return null;
}

function toFeature(item, location, ip) {
  const userAgent = extractUserAgent(item);
  const device =
    (typeof item.deviceLabel === "string" && item.deviceLabel.trim()) ||
    getDeviceLabel(userAgent, "Unknown");
  const appVersion =
    item.appVersion ||
    item.version ||
    (userAgent ? userAgent.match(/LunovaPlayer\/(\d+\.\d+\.\d+)/)?.[1] : "") ||
    "";

  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [location.longitude, location.latitude],
    },
    properties: {
      id: buildFeatureId(item, ip),
      city: location.city || "Unknown city",
      region: location.region || "",
      country: location.country || "",
      device,
      userAgent: userAgent || "",
      appVersion,
      lastSeen:
        item.lastSeen ||
        item.lastActive ||
        item.timestamp ||
        new Date().toISOString(),
      source: item.registrationId ? "registered" : "app",
      ipAddress: ip,
    },
  };
}

export function normalizeGeoJsonResponse(response) {
  const payload = response?.data ?? response;

  if (
    payload?.type === "FeatureCollection" &&
    Array.isArray(payload.features)
  ) {
    return {
      ...payload,
      type: "FeatureCollection",
      features: payload.features.map(enrichFeatureDevice),
    };
  }

  if (Array.isArray(payload?.features)) {
    return {
      ...payload,
      type: "FeatureCollection",
      features: payload.features.map(enrichFeatureDevice),
    };
  }

  return null;
}

function enrichFeatureDevice(feature) {
  if (!feature || typeof feature !== "object") return feature;

  const properties = { ...(feature.properties || {}) };
  const userAgent = extractUserAgent(properties);
  const deviceValue =
    typeof properties.device === "string" ? properties.device.trim() : "";
  const hasUsefulDevice =
    Boolean(deviceValue) &&
    deviceValue !== "Unknown" &&
    deviceValue !== userAgent;

  if (!hasUsefulDevice && userAgent) {
    properties.device = getDeviceLabel(userAgent, "Unknown");
    properties.userAgent = userAgent;
  } else if (!hasUsefulDevice) {
    properties.device = properties.device || "Unknown";
  }

  if (!properties.appVersion && userAgent) {
    properties.appVersion =
      userAgent.match(/LunovaPlayer\/(\d+\.\d+\.\d+)/)?.[1] || "";
  }

  return {
    ...feature,
    properties,
  };
}

export function extractPlayerUsers(response) {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.recentActivity)) return payload.recentActivity;

  return [];
}

export function filterLiveActivityUsers(recentActivity = []) {
  return recentActivity.filter((item) => item?.isOnline === true);
}

export function filterLivePlayerUsers(playerUsers = []) {
  return playerUsers.filter((item) => {
    if (item?.isOnline === false || item?.isLive === false) {
      return false;
    }

    return Boolean(item?.ipAddress || item?.ip || normalizeLocation(item));
  });
}

export async function buildClientActivityGeoJson({
  recentActivity = [],
  playerUsers = [],
  liveOnly = true,
  lookup = lookupIp,
} = {}) {
  const activityItems = liveOnly
    ? filterLiveActivityUsers(recentActivity)
    : recentActivity;
  const playerItems = liveOnly
    ? filterLivePlayerUsers(playerUsers)
    : playerUsers;
  const candidates = [];
  const seen = new Set();

  const addCandidate = (item) => {
    if (!item || typeof item !== "object") return;

    const ip = item.ipAddress || item.ip;
    const existingLocation = normalizeLocation(item);
    const dedupeKey = existingLocation
      ? `${existingLocation.longitude},${existingLocation.latitude}-${item.registrationId || item.id || ip || candidates.length}`
      : ip;

    if (!dedupeKey || seen.has(dedupeKey)) return;
    seen.add(dedupeKey);

    candidates.push({
      item,
      ip,
      existingLocation,
    });
  };

  activityItems.forEach(addCandidate);
  playerItems.forEach(addCandidate);

  if (candidates.length === 0) {
    return EMPTY_GEOJSON;
  }

  const lookupCache = new Map();
  const features = [];

  await Promise.all(
    candidates.map(async ({ item, ip, existingLocation }) => {
      let location = existingLocation;

      if (!location && ip && !isPrivateIp(ip)) {
        if (lookupCache.has(ip)) {
          location = lookupCache.get(ip);
        } else {
          const lookupResult = await lookup(ip);

          location =
            lookupResult?.latitude && lookupResult?.longitude
              ? {
                  city: lookupResult.city,
                  region: lookupResult.region,
                  country: lookupResult.countryCode || lookupResult.country,
                  latitude: lookupResult.latitude,
                  longitude: lookupResult.longitude,
                }
              : null;
          lookupCache.set(ip, location);
        }
      }

      if (!location) return;

      features.push(toFeature(item, location, ip));
    })
  );

  return {
    type: "FeatureCollection",
    features,
  };
}

export function summarizeClusterUsers(users = []) {
  const deviceCounts = {};
  let latestSeen = null;

  users.forEach((user) => {
    const device = user?.device || "Unknown";

    deviceCounts[device] = (deviceCounts[device] || 0) + 1;

    if (user?.lastSeen) {
      const seenAt = new Date(user.lastSeen).getTime();

      if (!latestSeen || seenAt > latestSeen) {
        latestSeen = seenAt;
      }
    }
  });

  const sortedDevices = Object.entries(deviceCounts).sort(
    (a, b) => b[1] - a[1]
  );

  return {
    deviceCounts: sortedDevices,
    lastActivityLabel: latestSeen
      ? formatDistanceToNow(new Date(latestSeen), { addSuffix: true })
      : null,
  };
}

export function formatLocationLabel(city, region) {
  if (city && region) return `${city}, ${region}`;
  if (city) return city;
  if (region) return region;

  return "Unknown location";
}
